/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import './MainFields.scss'
import moment from 'moment';
import { debounce } from 'lodash';
import { getOrderStatuses } from '../../../utils/helpers';
import { CHRONICITY_MAP, ORDER_CANCELLED, PRESORTED, PRESORTED_ORDER_TYPE, STRIPS, STRIPS_ORDER_TYPE } from '../../../utils/constants';
import CancellationReasonPrompt from '../../../components/CancellationReasonPrompt';
import { useSourcesData } from '../../../hooks/fetch-hooks/get-sources';
import Select from 'react-select'

const chronicityOptions = Object.keys(CHRONICITY_MAP).map((key) => {
    return { label: CHRONICITY_MAP[key], value: key }
})

const MainFields = ({ orderType, messages, status, updateOrderData, startDate, prescriptionDate, endDate, noOfDays, drName, userNotes, source, channel, isEditable, partnerId, partners, partnersLoading, chronicity, isEazy, mrno, paymentStatus, inventoryId, billNumber, onFetchBillNumber }) => {
    const debounceOnChange = React.useCallback(debounce(updateOrderData, 200), []);
    const orderStatuses = getOrderStatuses();
    const [notes, setNotes] = useState('');
    const [selectedChronicity, setSelectedChronicity] = useState([]);
    const [mrnoInput, setMrnoInput] = useState('');
    // Holds the cancelled selection while a reason is collected. Null = show the saved
    // status; the change is not persisted until ops confirms.
    const [statusDraft, setStatusDraft] = useState(null);
    const [pendingCancel, setPendingCancel] = useState(false);
    const [cancelSaving, setCancelSaving] = useState(false);

    const handleStatusChange = (e) => {
        const next = e.target.value;

        if (next === ORDER_CANCELLED) {
            setStatusDraft(next);
            setPendingCancel(true);
            return;
        }

        setStatusDraft(null);
        setPendingCancel(false);
        updateOrderData(next, 'status');
    };

    const { channels, isLoading: channelsLoading, getSourcesForChannels } = useSourcesData();

    // Get available channels for the selected source
    const availableSources = channel ? getSourcesForChannels(channel) : [];

    useEffect(() => {
        setNotes(userNotes)
    }, [userNotes])

    useEffect(() => {
        setSelectedChronicity(chronicity?.map((ch) => {
            return {
                label: CHRONICITY_MAP[ch],
                value: ch
            }
        }) || [])
    }, [chronicity])

    // Set default channel when channel data loads and no source is set
    // useEffect(() => {
    //     if (channels.length > 0 && !channel) {
    //         updateOrderData(channels[0], 'channel');
    //     }
    // }, [channels, channel, updateOrderData]);

    const handleChronicityChange = async (chronicity) => {
        setSelectedChronicity(chronicity);
        await updateOrderData(chronicity.map((ch) => ch.value), "chronicity")
    }

    return (
        <div className='bg-[white] px-5 py-5 rounded-lg border border-gray-300' >
            {
                messages?.map((message, key) => {
                    return (
                        <p key={key} className='text-[red] text-bold' > {message.text}</p>
                    )
                })
            }

            <br />
            <div className='relative bg-[white] rounded-lg flex flex-row flex-wrap gap-5 justify-left' >
                <div className='flex flex-col flex-grow' >
                    <label className='main-fields_label' >Doctor Name</label>
                    <input type='text' name='drName' value={drName} onChange={(e) => updateOrderData(e.target.value, e.target.name)} className='rounded-md main-fields_input' />
                </div>
                <div className='flex flex-col flex-grow' >
                    <label className='main-fields_label' >Prescription Date</label>
                    <input type='date' name='date' value={moment(prescriptionDate).format('YYYY-MM-DD')} onChange={(e) => updateOrderData(moment(e.target.value).format(), e.target.name)} className='rounded-md main-fields_input' />
                </div>
                <div className='flex flex-col flex-grow' >
                    <label className='main-fields_label' >Start Date</label>
                    <input type='date' name='startDate' value={startDate} onChange={(e) => updateOrderData(e.target.value, e.target.name)} className='rounded-md main-fields_input' />
                </div>
                <div className='flex flex-col flex-grow' >
                    <label className='main-fields_label' >Duration</label>
                    <input type='number' min={1} name='noOfDays' value={noOfDays} onChange={(e) => updateOrderData(parseInt(e.target.value), e.target.name)} className='rounded-md main-fields_input' />
                </div>
                <div className='flex flex-col flex-grow' >
                    <label className='main-fields_label' >End Date</label>
                    <input type='date' name='endDate' disabled value={endDate} className='rounded-md main-fields_input opacity-50' />
                </div>
                <div className='flex flex-col flex-grow' >
                    <label className='main-fields_label' >Status</label>
                    <select className="rounded-lg h-10" name='status' value={statusDraft ?? status} onChange={handleStatusChange} >
                        {
                            orderStatuses && (orderType.includes(PRESORTED) ? orderStatuses[PRESORTED_ORDER_TYPE] : orderStatuses[STRIPS_ORDER_TYPE]).map((statusOption) => {
                                return (
                                    <option key={statusOption} value={statusOption}>{statusOption}</option>
                                )
                            })
                        }
                    </select>
                    {pendingCancel && (
                        <CancellationReasonPrompt
                            variant="inline"
                            saving={cancelSaving}
                            onConfirm={async (reason) => {
                                setCancelSaving(true);
                                try {
                                    await updateOrderData(reason, 'cancelWithReason');
                                    setPendingCancel(false);
                                    setStatusDraft(null);
                                } catch (err) {
                                    // Leave the prompt open so the reason ops typed is not
                                    // lost and the cancel can be retried.
                                } finally {
                                    setCancelSaving(false);
                                }
                            }}
                            onDismiss={() => {
                                if (cancelSaving) return;
                                setPendingCancel(false);
                                setStatusDraft(null);
                            }}
                        />
                    )}
                </div>
                <div className='flex flex-col' >
                    <label className='main-fields_label' >Channel</label>
                    <select
                        className="rounded-lg h-10"
                        name='channel'
                        value={channel || ''}
                        onChange={(e) => updateOrderData(e.target.value, e.target.name)}
                        disabled={channelsLoading || !isEditable}
                    >
                        <option value="">Select Channel</option>
                        {channels.map((channelOption) => (
                            <option key={channelOption} value={channelOption}>
                                {channelOption}
                            </option>
                        ))}
                    </select>
                </div>
                <div className='flex flex-col' >
                    <label className='main-fields_label' >Source</label>
                    <select
                        className="rounded-lg h-10"
                        name='source'
                        value={source || ''}
                        onChange={(e) => updateOrderData(e.target.value, e.target.name)}
                        disabled={!channel || availableSources.length === 0 || !isEditable}
                    >
                        <option value="">
                            {!channel ? 'Select Channel First' : 'Select Source'}
                        </option>
                        {availableSources.map((sourceOption) => (
                            <option key={sourceOption} value={sourceOption}>
                                {sourceOption}
                            </option>
                        ))}
                    </select>
                </div>
                {source === "B2B" && <div className='flex flex-col' >
                    <label className='main-fields_label' >Partner</label>
                    <select
                        className="rounded-lg h-10"
                        name='partnerId'
                        value={partnerId}
                        onChange={(e) => {
                            const newPartnerId = e.target.value ? parseInt(e.target.value) : null;

                            // Update partnerId first
                            updateOrderData(newPartnerId, e.target.name);

                            const currentOrderTypeBase = orderType?.replace('PARTNER_', '').replace('INTERNAL_', '') || '';
                            // Then update the order type based on partner selection
                            if (!newPartnerId) {
                                // No partner selected - convert to INTERNAL
                                if (currentOrderTypeBase) {
                                    updateOrderData(`INTERNAL_${currentOrderTypeBase}`, 'type');
                                }
                            } else {
                                // Partner selected - convert to PARTNER
                                if (currentOrderTypeBase) {
                                    updateOrderData(`PARTNER_${currentOrderTypeBase}`, 'type');
                                }
                            }
                        }}
                        disabled={partnersLoading}
                    >
                        <option value="">
                            {partnersLoading ? 'Loading Partners...' : 'Select Partner'}
                        </option>
                        {partners.map((partnerOption) => (
                            <option key={partnerOption.id} value={partnerOption.id}>
                                {partnerOption.name}
                            </option>
                        ))}
                    </select>
                </div>}
                <div className='flex flex-col' >
                    <label className='main-fields_label'>Order Type</label>
                    <select
                        className="rounded-lg h-10"
                        name="type"
                        required
                        value={orderType?.replace('INTERNAL_', '').replace('PARTNER_', '') || ''}
                        onChange={(e) => {
                            const currentPrefix = orderType?.includes('PARTNER_') ? 'PARTNER_' : 'INTERNAL_';
                            updateOrderData(`${currentPrefix}${e.target.value}`, e.target.name);
                        }}
                    >
                        <option value="">Select Order Type</option>
                        <option value={PRESORTED}>Presorted</option>
                        <option value={STRIPS}>Strips</option>
                    </select>
                </div>

                <div className='flex flex-col min-w-[250px]' >
                    <label className='main-fields_label'>Chronicity</label>
                    <Select options={chronicityOptions} isMulti onChange={handleChronicityChange} value={selectedChronicity} />
                </div>

                {/* Read-only: payment state as DECLARED BY THE PARTNER at intake, not PillUp's
                    own payment records (these orders are collected on the partner's side, so
                    they have no payments/paymentItems rows). Rendered unconditionally with a
                    dash so the field is still visible on orders placed before it was captured
                    — hiding it entirely made it look like the feature was missing. */}
                <div className='flex flex-col' >
                    <label className='main-fields_label'>Payment Status</label>
                    <input
                        type='text'
                        value={paymentStatus || '—'}
                        disabled
                        readOnly
                        title={paymentStatus ? 'Declared by the partner when the order was placed' : 'Not captured for this order'}
                        className='rounded-md main-fields_input opacity-50'
                    />
                </div>

                {/* Read-only: the eVital order this order is mapped to, and its bill number
                    (needed before an OCS can be generated). */}
                {inventoryId && (
                    <>
                        <div className='flex flex-col' >
                            <label className='main-fields_label'>Evital Inventory ID</label>
                            <input type='text' value={inventoryId} disabled readOnly className='rounded-md main-fields_input opacity-50' />
                        </div>
                        <div className='flex flex-col' >
                            <label className='main-fields_label'>Evital Bill No</label>
                            <div className='flex flex-row gap-2 items-center'>
                                <input type='text' value={billNumber || 'Not available'} disabled readOnly className='rounded-md main-fields_input opacity-50' />
                                {!billNumber && onFetchBillNumber && (
                                    <button type='button' className='text-xs p-2 rounded-md bg-[#f4a261] text-white-50 whitespace-nowrap' onClick={onFetchBillNumber}>
                                        Fetch Bill Number from Evital
                                    </button>
                                )}
                            </div>
                        </div>
                    </>
                )}

            </div>
            {isEazy && !mrno && (
                <div className='flex flex-col flex-grow mt-2'>
                    <label className='main-fields_label'>MRN Number</label>
                    <div className='flex flex-row gap-2'>
                        <input
                            type='text'
                            placeholder='Enter MRN Number'
                            value={mrnoInput}
                            onChange={(e) => setMrnoInput(e.target.value)}
                            className='rounded-md main-fields_input flex-grow'
                        />
                        <button
                            className='px-4 py-2 bg-blue-500 text-white-50 rounded-md disabled:opacity-50'
                            disabled={!mrnoInput.trim()}
                            onClick={() => updateOrderData(mrnoInput.trim(), 'mrno')}
                        >
                            Save
                        </button>
                    </div>
                </div>
            )}
            {isEazy && mrno && (
                <div className='flex flex-col flex-grow mt-2'>
                    <label className='main-fields_label'>MRN Number</label>
                    <input type='text' value={mrno} disabled className='rounded-md main-fields_input opacity-50' />
                </div>
            )}
            <div className='flex flex-col flex-grow mt-2' >
                <label className='main-fields_label' >User Notes</label>
                <textarea name='notes' rows={4} defaultValue={notes} onChange={(e) => debounceOnChange(e.target.value, e.target.name)} className='rounded-md' />
            </div>
        </div >
    )
}

export default MainFields;